import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, Key, Check } from 'lucide-react';
import { FloatingDoodles } from '../components/Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const location = useLocation();
  const [step, setStep] = useState('email'); // email, token, done
  const [email, setEmail] = useState('');
  const [demoToken, setDemoToken] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Auto-fill token from email link ?token=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    if (t) {
      setToken(t);
      setStep('token');
    }
  }, [location.search]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(data.message);
      if (data.demo_token) {
        setDemoToken(data.demo_token);
        setToken(data.demo_token); // Auto-fill in demo mode
      }
      setStep('token');
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setMessage(data.message);
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center p-4 relative">
      <FloatingDoodles />
      <div className="w-full max-w-md relative z-10">
        <div className="neo-card p-8">
          {step === 'email' && (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#A2D2FF] border-3 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] mb-4">
                  <Mail className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold font-['Fredoka']">Forgot Password?</h2>
                <p className="text-gray-500 text-sm mt-1">Enter your email to get a reset link</p>
              </div>
              {error && <div className="mb-4 p-3 bg-[#FF6B6B] text-white rounded-lg border-2 border-black font-semibold">{error}</div>}
              <form onSubmit={handleRequestReset} className="space-y-4">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="neo-input w-full" placeholder="your@email.com" required data-testid="forgot-email-input" />
                <button type="submit" disabled={loading} className="neo-btn w-full py-3 bg-[#A2D2FF]" data-testid="forgot-submit-btn">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}

          {step === 'token' && (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B9FBC0] border-3 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] mb-4">
                  <Key className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold font-['Fredoka']">Reset Password</h2>
              </div>
              
              {/* Demo mode notice */}
              {demoToken && (
                <div className="mb-4 p-3 bg-[#FDF0D5] rounded-lg border-2 border-black text-sm">
                  <p className="font-bold mb-1">Backup-läge</p>
                  <p className="text-gray-600">E-postleverans misslyckades — token har fyllts i automatiskt.</p>
                </div>
              )}
              {!demoToken && token && (
                <div className="mb-4 p-3 bg-[#E8F5FF] rounded-lg border-2 border-black text-sm">
                  <p className="font-bold mb-1">📧 Token från e-post</p>
                  <p className="text-gray-600">Skriv in ett nytt lösenord nedan.</p>
                </div>
              )}
              
              {error && <div className="mb-4 p-3 bg-[#FF6B6B] text-white rounded-lg border-2 border-black font-semibold">{error}</div>}
              
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-1 font-['Fredoka']">Reset Token</label>
                  <input type="text" value={token} onChange={(e) => setToken(e.target.value)} className="neo-input w-full text-xs" placeholder="Paste reset token..." required data-testid="reset-token-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1 font-['Fredoka']">New Password</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="neo-input w-full" placeholder="At least 6 characters" required minLength={6} data-testid="reset-password-input" />
                </div>
                <button type="submit" disabled={loading} className="neo-btn w-full py-3 bg-[#B9FBC0]" data-testid="reset-submit-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          {step === 'done' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B9FBC0] border-3 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] mb-4">
                <Check className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold font-['Fredoka'] mb-2">Password Reset!</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <Link to="/login" className="neo-btn px-8 py-3 bg-[#A2D2FF] inline-block" data-testid="back-to-login-btn">
                Back to Login
              </Link>
            </div>
          )}

          {step !== 'done' && (
            <div className="mt-4 text-center">
              <Link to="/login" className="text-[#A2D2FF] font-bold hover:underline flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
